import * as React from 'react';
import {Link as RoutedLink} from 'react-router-dom';

import type {ColorName} from 'shells/colors/colors';
import type {CueCardCtaAction} from 'shells/cue-card/cue-card';
import {Link} from 'shells/link/link';

import {OpenableModals} from '../../openable-modals/openable-modals';
import type {OpenableModalId} from '../../openable-modals/openable-modals';

// Options for CueCard locations
export type CueCardLocation =
    | 'glossary-only'
    | 'banner'
    | 'list-item'
    | 'settings-sidebar'
    | 'onboarding-sidebar';

// Options for CueCard background colors
type CueCardBackgroundColor = ColorName; // TODO: Determine subset of ColorNames that can be used

// Structure of a CueCard
export type CueCard = {
    settingsKey: string;
    title: string;
    description?: string | React.ComponentType<any>;
    location: CueCardLocation;
    thumbnailPosition?: 'centered' | 'full' | 'no-resize';
    thumbnailPath?: string; // All CUE card thumbnails should be stored in /include/images/onboarding,
    backgroundColor?: CueCardBackgroundColor;
    cta?: {
        text?: string;
        actionType?: CueCardCtaAction; // 'external-link' | 'routed-link' | 'modal' | 'video',
        newTab?: boolean;
        url?: string;
        openableModalId?: OpenableModalId;
        intercomArticleId?: number;
        youTubeVideoId?: string;
    };
    isDismissable?: boolean; // defaults to true; set to false to prevent the user from dismissing the card
    instanceWide?: boolean; // true if dismissible for entire instance; false, or omitted, if dismissible per user,
    adminOnly?: boolean; // true if only visible to admin users,
    requiredParam?: string; // If provided, the CUE card will only be shown if the requiredParam is present in the URL,
    featureFlag?: string; // If provided, the CUE card will only be shown if the feature flag is present
};

export type PageCueCardCollection = {
    sidebarTitle?: string;
    cards: CueCard[];
};

type CueCardCollection = {
    [key: string]: PageCueCardCollection;
};

export const CUE_CARD_COLLECTION: CueCardCollection = {
    /* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
     * Example CUE card collection for page
     * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */
    '/sample-path': {
        sidebarTitle: 'This is the title of the sample glossary for this page path',
        cards: [
            {
                settingsKey: 'sample_settings_key',
                title: 'Sample CUE card title for this page’s glossary',
                description: 'This is a nice description for this CUE card',
                location: 'glossary-only',
                thumbnailPath: '/include/images/onboarding/competitor-iq-ball-roll.svg',
                backgroundColor: 'tagBlueLt',
                cta: {
                    text: 'Sample CTA text',
                    actionType: 'external-link',
                    url: 'https://www.google.com',
                },
            },
        ],
    },
    /* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
     * Email marketing CUE cards
     * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */
    '/email-marketing/analytics': {
        sidebarTitle:
            'Analytics brings you additional information about what your leads were doing before they became a lead',
        cards: [
            {
                settingsKey: 'analytics_onboarding_modal',
                title: 'Learn where your leads come from with Nutshell Analytics',
                description:
                    'Knowing where your leads come from can help your team make the right sales and marketing decisions.',
                location: 'banner',
                thumbnailPath: '/include/images/onboarding/analytics-info.svg',
                backgroundColor: 'plumXlt',
                cta: {
                    text: 'Learn more',
                    actionType: 'modal',
                    openableModalId: OpenableModals.ANALYTICS_ONBOARDING_MODAL,
                },
            },
            {
                settingsKey: 'analytics_setup_wizard',
                title: 'Get started by setting up tracking',
                description:
                    'We provide you with a small code snippet to embed on your website to start tracking site visitors.',
                location: 'glossary-only',
                thumbnailPath: '/include/images/onboarding/embed-snippet.svg',
                backgroundColor: 'tagOrangeLt',
                cta: {
                    text: 'Set up tracking',
                    openableModalId: OpenableModals.EMBED_ANALYTICS_MODAL,
                },
            },
        ],
    },
    '/email-marketing/broadcasts': {
        sidebarTitle: 'Broadcasts are one-time email messages sent out to an audience',
        cards: [
            {
                settingsKey: 'broadcasts_info_modal',
                title: 'What are broadcasts?',
                description: 'Design and send one-time email messages to an audience.',
                location: 'banner',
                thumbnailPath: '/include/images/onboarding/single-purple-envelope-email.svg',
                backgroundColor: 'plumXlt',
                cta: {
                    text: 'Learn more',
                    openableModalId: OpenableModals.BROADCAST_INFO_MODAL,
                    actionType: 'modal',
                },
            },
            {
                settingsKey: 'configure_dns_for_gmail',
                title: 'Configure additional DNS records to continue sending outreach to Gmail users',
                description:
                    'Starting in February, Google, Yahoo, and AOL will begin requiring DMARC settings',
                location: 'banner',
                thumbnailPath: '/include/images/onboarding/warning-icon.svg',
                backgroundColor: 'yellow',
                cta: {
                    text: 'Learn more',
                    actionType: 'external-link',
                    url: 'https://www.nutshell.com/blog/upcoming-dmarc-email-authentication-requirements',
                },
                adminOnly: true,
            },
        ],
    },
    '/email-marketing/drip-sequences': {
        sidebarTitle:
            'Drip sequences are a series of messages that automatically send based on how you configure it',
        cards: [
            {
                settingsKey: 'drip_sequences_info_modal',
                title: 'What is a drip sequence?',
                description: 'Automate a series of messages to send at a set cadence.',
                location: 'banner',
                thumbnailPath: '/include/images/onboarding/drip-sequence-doodle.svg',
                backgroundColor: 'plumXlt',
                cta: {
                    text: 'Learn more',
                    openableModalId: OpenableModals.DRIP_SEQUENCE_INFO_MODAL,
                    actionType: 'modal',
                },
            },
        ],
    },
    '/email-marketing/newsletters': {
        sidebarTitle: 'Newsletters are an ongoing series of messages that follow a similar theme',
        cards: [
            {
                settingsKey: 'newsletter_info_modal',
                title: 'What are newsletters?',
                description: 'Learn about sending regular correspondence to an audience',
                location: 'banner',
                thumbnailPath: '/include/images/onboarding/open-envelope.svg',
                backgroundColor: 'plumXlt',
                cta: {
                    text: 'Learn more',
                    openableModalId: OpenableModals.NEWSLETTER_INFO_MODAL,
                    actionType: 'modal',
                },
            },
        ],
    },
    '/email-marketing/summary': {
        sidebarTitle:
            'Campaigns give you all the email marketing tools you need to build an audience',
        cards: [
            {
                settingsKey: 'campaign_onboarding_modal',
                title: 'Design, send, and report on your campaigns',
                description: 'Send broadcasts, drip sequences, and regular newsletters.',
                location: 'banner',
                thumbnailPath: '/include/images/onboarding/purple-email-envelope-airplane.svg',
                backgroundColor: 'plumXlt',
                cta: {
                    text: 'Learn more',
                    openableModalId: OpenableModals.CAMPAIGNS_ONBOARDING_MODAL,
                    actionType: 'modal',
                },
            },
            {
                settingsKey: 'configure_dns_for_gmail',
                title: 'Configure additional DNS records to continue sending outreach to Gmail users',
                description:
                    'Starting in February, Google, Yahoo, and AOL will begin requiring DMARC settings',
                location: 'banner',
                thumbnailPath: '/include/images/onboarding/warning-icon.svg',
                backgroundColor: 'yellow',
                cta: {
                    text: 'Learn more',
                    actionType: 'external-link',
                    url: 'https://www.nutshell.com/blog/upcoming-dmarc-email-authentication-requirements',
                },
                adminOnly: true,
            },
        ],
    },
    '/companies': {
        sidebarTitle: 'Companies hold information about the organizations you do business with',
        cards: [
            {
                settingsKey: 'companies_import',
                title: 'Add your existing data to Nutshell',
                description:
                    'Transfer information from another tool or request that our friendly team do it for you. We have all sorts of resources for importing your data.',
                location: 'glossary-only',
                thumbnailPath: '/include/images/onboarding/companies-people.svg',
                backgroundColor: 'blueLt',
                cta: {
                    text: 'Import data',
                    actionType: 'routed-link',
                    url: '/setup/import',
                },
            },
        ],
    },
    '/people': {
        sidebarTitle: 'People in Nutshell are the individuals you do business with',
        cards: [
            {
                settingsKey: 'people_import',
                title: 'Add your existing data to Nutshell',
                description:
                    'Transfer information from another tool or request that our friendly team do it for you. We have all sorts of resources for importing your data.',
                location: 'glossary-only',
                thumbnailPath: '/include/images/onboarding/audience.svg',
                backgroundColor: 'blueLt',
                cta: {
                    text: 'Import data',
                    actionType: 'routed-link',
                    url: '/setup/import',
                },
            },
        ],
    },
    '/leads': {
        sidebarTitle: 'Leads represent your business deals',
        cards: [
            {
                settingsKey: 'leads_import',
                title: 'Add your existing data to Nutshell',
                description:
                    'Transfer information from another tool or request that our friendly team do it for you. We have all sorts of resources for importing your data.',
                location: 'glossary-only',
                thumbnailPath: '/include/images/onboarding/leads.svg',
                backgroundColor: 'lead',
                cta: {
                    text: 'Import data',
                    actionType: 'routed-link',
                    url: '/setup/import',
                },
            },
            {
                settingsKey: 'customize_lead_card',
                title: 'Choose which fields to display on your lead cards',
                description:
                    'Easily customize your lead cards by selecting and arranging up to eight fields, tailored to your team’s unique workflow.',
                location: 'glossary-only',
                thumbnailPath: '/include/images/onboarding/lead-arrow.svg',
                backgroundColor: 'plum',
                cta: {
                    text: 'Learn more',
                    intercomArticleId: 9014137,
                },
                adminOnly: true,
            },
        ],
    },
    '/setup/domains': {
        sidebarTitle:
            'Configure additional DNS records to continue sending outreach to Gmail users',
        cards: [
            {
                settingsKey: 'configure_dns_for_gmail',
                title: 'Configure additional DNS records to continue sending outreach to Gmail users',
                description:
                    'Starting in February, Google, Yahoo, and AOL will begin requiring DMARC settings',
                location: 'banner',
                thumbnailPath: '/include/images/onboarding/warning-icon.svg',
                backgroundColor: 'yellow',
                cta: {
                    text: 'Learn more',
                    actionType: 'external-link',
                    url: 'https://www.nutshell.com/blog/upcoming-dmarc-email-authentication-requirements',
                },
                adminOnly: true,
            },
        ],
    },
    '/email-marketing/visitors/list': {
        sidebarTitle:
            'VisitorIQ identifies the companies and individuals exploring your website before they even fill out a form',
        cards: [
            {
                settingsKey: 'what_is_visitor_iq',
                title: 'What is VisitorIQ?',
                description:
                    'A powerful prospecting tool to track and convert website visitors into sales opportunities.',
                location: 'glossary-only',
                thumbnailPath: '/include/images/onboarding/visitor-iq.svg',
                backgroundColor: 'plumXlt',
                cta: {
                    text: 'Learn more',
                    openableModalId: OpenableModals.VISITOR_IQ_ONBOARDING_MODAL,
                    actionType: 'modal',
                },
            },
        ],
    },
    '/email-marketing/visitors/settings': {
        sidebarTitle:
            'VisitorIQ identifies the companies and individuals exploring your website before they even fill out a form',
        cards: [
            {
                settingsKey: 'what_is_visitor_iq',
                title: 'What is VisitorIQ?',
                description:
                    'A powerful prospecting tool to track and convert website visitors into sales opportunities.',
                location: 'glossary-only',
                thumbnailPath: '/include/images/onboarding/visitor-iq.svg',
                backgroundColor: 'plumXlt',
                cta: {
                    text: 'Learn more',
                    openableModalId: OpenableModals.VISITOR_IQ_ONBOARDING_MODAL,
                    actionType: 'modal',
                },
            },
        ],
    },
    '/email-marketing/forms': {
        sidebarTitle:
            'Forms can be embedded on a site to collect contact information and automatically create new people, companies, and leads',
        cards: [
            {
                settingsKey: 'what_are_forms',
                title: 'What are forms?',
                description: 'Embed on a site to collect contacts and other information.',
                location: 'banner',
                thumbnailPath: '/include/images/onboarding/forms.svg',
                backgroundColor: 'plumXlt',
                cta: {
                    text: 'Learn more',
                    openableModalId: OpenableModals.FORMS_INFO_MODAL,
                    actionType: 'modal',
                },
            },
        ],
    },
    '/email-marketing/connected-forms': {
        sidebarTitle:
            'Connected forms let you capture submission data from forms created with other tools',
        cards: [
            {
                settingsKey: 'what_are_connected_forms',
                title: 'What are connected forms?',
                description: 'Capture submission data from forms created with other tools.',
                location: 'banner',
                thumbnailPath: '/include/images/onboarding/connected-forms.svg',
                backgroundColor: 'plumXlt',
                cta: {
                    text: 'Learn more',
                    openableModalId: OpenableModals.CONNECTED_FORMS_INFO_MODAL,
                    actionType: 'modal',
                },
            },
        ],
    },
    '/email-marketing/competitors/charts': {
        sidebarTitle:
            'CompetitorSpy is a powerful tool for comparing your website’s performance with up to five competitors’',
        cards: [
            {
                settingsKey: 'elevate_insights',
                title: 'Elevate your insights with CompetitorSpy',
                description:
                    'Gain an edge over your top 5 competitors with detailed competitive analysis.',
                location: 'banner',
                thumbnailPath: '/include/images/onboarding/competitor-iq-ball-roll.svg',
                backgroundColor: 'plumXlt',
                cta: {
                    text: 'Learn more',
                    openableModalId: OpenableModals.COMPETITOR_IQ_INFO_MODAL,
                    actionType: 'modal',
                },
            },
        ],
    },
    '/email-marketing/competitors/settings': {
        sidebarTitle:
            'CompetitorSpy is a powerful tool for comparing your website’s performance with up to five competitors’',
        cards: [
            {
                settingsKey: 'elevate_insights',
                title: 'Elevate your insights with CompetitorSpy',
                description:
                    'Gain an edge over your top 5 competitors with detailed competitive analysis.',
                location: 'banner',
                thumbnailPath: '/include/images/onboarding/competitor-iq-ball-roll.svg',
                backgroundColor: 'plumXlt',
                cta: {
                    text: 'Learn more',
                    openableModalId: OpenableModals.COMPETITOR_IQ_INFO_MODAL,
                    actionType: 'modal',
                },
            },
        ],
    },
    '/email-marketing/landing-pages': {
        sidebarTitle: 'Begin creating engaging pages',
        cards: [
            {
                settingsKey: 'landing-pages',
                title: 'Begin creating engaging pages',
                description:
                    'Explore our comprehensive features for designing landing pages that deliver results.',
                location: 'banner',
                thumbnailPosition: 'no-resize',
                thumbnailPath: '/include/images/onboarding/landing-pages-video.png',
                backgroundColor: 'yellow',
                cta: {
                    text: 'Learn more',
                    actionType: 'video',
                    youTubeVideoId: 'H96Tl5iEbQw',
                },
            },
        ],
    },
    '/setup/ai-features': {
        cards: [
            {
                settingsKey: 'ai_outcomes_vs_assists',
                title: 'AI outcome versus AI assists',
                description:
                    'AI assists help you stay productive by organizing and summarizing your CRM activity, with unlimited usage included in your plan. AI outcomes find new insights and research for you, with a usage allowance that scales with your team and plan',
                location: 'banner',
                thumbnailPath: '/include/images/ai/ai-outcomes-vs-assists.svg',
                thumbnailPosition: 'no-resize',
                backgroundColor: 'blueLt',
                cta: {
                    text: 'Learn more',
                    actionType: 'external-link',
                    url: 'https://support.nutshell.com/en/articles/13762956-understanding-ai-assists-and-ai-outcomes-in-nutshell',
                },
                adminOnly: false,
            },
        ],
    },
    '/setup/sales-automation': {
        sidebarTitle: 'Pipelines track your sales processes through different stages',
        cards: [
            {
                settingsKey: 'defaultStagesetOnboardingWizard',
                title: 'Design a pipeline around your sales process',
                description:
                    'Answer a few questions about your business and we’ll help you automatically generate a pipeline.',
                location: 'banner',
                thumbnailPath: '/include/images/onboarding/lead-arrow-group.svg',
                backgroundColor: 'yellow',
                cta: {
                    text: 'Get started',
                    openableModalId: OpenableModals.DESIGN_PIPELINE_WIZARD,
                    actionType: 'modal',
                },
                adminOnly: true,
            },
            {
                settingsKey: 'templateDownloads',
                title: 'What makes a good pipeline?',
                description: 'We have 16 examples of sales pipelines to get you started.',
                location: 'banner',
                thumbnailPosition: 'full',
                thumbnailPath: '/include/images/onboarding/pipeline-templates.jpg',
                backgroundColor: 'blueLt',
                cta: {
                    text: 'View templates',
                    actionType: 'routed-link',
                    url: '/dotcom/process-templates',
                },
            },
        ],
    },
    '/setup/index': {
        cards: [
            {
                settingsKey: 'fiscal_year',
                title: 'Fiscal year',
                description:
                    'We use your company’s fiscal year to calculate yearly quotas, determine your current quarter, and provide the right annual time frames in your reports.',
                location: 'settings-sidebar',
                adminOnly: true,
            },
            {
                settingsKey: 'business_hours',
                title: 'Business hours',
                description: () => (
                    <div>
                        Nutshell calculates deadlines and delays based on your business hours so
                        that tasks and automated emails occur during your workday. These times are
                        shown in each individual’s local timezone, which is set up on the{' '}
                        <Link
                            // @ts-expect-error
                            as={RoutedLink}
                            variant='primary'
                            size='small'
                            to='/my-account/settings'
                            newTab={true}
                        >
                            Your Account
                        </Link>{' '}
                        page.
                    </div>
                ),
                location: 'settings-sidebar',
                adminOnly: true,
            },
        ],
    },
    '/setup/security': {
        cards: [
            {
                settingsKey: 'roles_and_permissions',
                title: 'Roles and permissions for different users',
                description:
                    'In the ‘Roles & permission’ section, admin users have the ability to grant or restrict access to specific features in Nutshell by configuring permissions based on roles.',
                location: 'settings-sidebar',
                cta: {
                    text: 'Manage roles & permissions',
                    actionType: 'routed-link',
                    url: '/setup/user?manageRolePermissions',
                },
                adminOnly: true,
            },
        ],
    },
    '/email/inbox': {
        cards: [
            {
                settingsKey: 'shared_email_inbox',
                title: 'Shared email inboxes',
                description:
                    'Sales teams receive leads via email and collaborate to automate lead intake.',
                location: 'banner',
                thumbnailPath: '/include/illustrations/EmailBannerCard.svg',
                backgroundColor: 'blueXlt',
                cta: {
                    text: 'Get started',
                    actionType: 'routed-link',
                    url: '/inbox/settings/email',
                },
            },
        ],
    },
    '/inbox/settings/facebook': {
        sidebarTitle: 'Configure Instagram message control settings',
        cards: [
            {
                settingsKey: 'configure_instagram_message_control_settings',
                title: 'Configure Instagram message control settings',
                description:
                    'Instagram professional accounts must allow access to messages for connected tools',
                location: 'banner',
                thumbnailPath: '/include/images/onboarding/instagram-bubble.svg',
                backgroundColor: 'plumXlt',
                cta: {
                    text: 'Learn more',
                    actionType: 'external-link',
                    url: 'https://support.nutshell.com/en/articles/11028248-facebook-messenger-instagram-dms-integration',
                },
            },
        ],
    },
    '/inbox/settings/sms': {
        cards: [
            {
                settingsKey: 'enrollment_required',
                title: 'Why is enrollment required?',
                description:
                    'U.S. cell phone carriers require that you enroll in their SMS program before you can send text messages. This form collects information needed to approve your messages with our infrastructure provider.',
                location: 'settings-sidebar',
                adminOnly: true,
                cta: {
                    text: 'SMS enrollment guide',
                    actionType: 'external-link',
                    url: 'https://support.nutshell.com/en/articles/12921113-nutshell-sms-tips-for-a-successful-sms-enrollment',
                },
            },
            {
                settingsKey: 'what_happens',
                title: 'What happens after the submission of my form?',
                description:
                    'We will review your form and submit it to our infrastructure provider. This typically takes 5-10 business days, although it can be quite variable. We will notify you via email when we receive approval or if changes are needed.',
                location: 'settings-sidebar',
                adminOnly: true,
            },
            {
                settingsKey: 'sms_phone_number',
                title: 'Do I get my own phone number?',
                description:
                    'Yes, your team will have a dedicated phone number for sending and receiving text messages. You will be able to pick a number from your area code, and your entire team will share the same number.',
                location: 'settings-sidebar',
                adminOnly: true,
            },
        ],
    },
    '/inbox/settings/agents': {
        cards: [
            {
                settingsKey: 'change_status',
                title: 'How to change your status',
                description:
                    'In order to communicate with your website visitors, you will need to change your status.',
                location: 'settings-sidebar',
                thumbnailPath: '/include/images/chat/nav-status-online.svg',
                backgroundColor: 'plumXlt',
                cta: {
                    text: 'Learn more',
                    actionType: 'modal',
                    openableModalId: OpenableModals.AGENT_STATUS_INFO_MODAL,
                },
                adminOnly: true,
            },
        ],
    },
    '/inbox/settings/email': {
        cards: [
            {
                settingsKey: 'setup_your_shared_email_address',
                title: 'Shared email inboxes',
                description:
                    'Sales teams receive leads via email and collaborate to automate lead intake.',
                location: 'settings-sidebar',
                thumbnailPosition: 'full',
                thumbnailPath: '/include/illustrations/email-inbox-settings-header.svg',
                backgroundColor: 'blueXlt',
                cta: {
                    text: 'Learn more',
                    actionType: 'external-link',
                    url: 'https://support.nutshell.com/en/articles/14464281-shared-email-inbox-in-nutshell',
                },
                adminOnly: true,
            },
        ],
    },
    '/setup/user': {
        cards: [
            {
                settingsKey: 'pending_users',
                title: 'Pending users',
                description:
                    'Pending users are automatically created when email or calendar sync sees new email addresses controlled by your organization. You are not billed for these users and they cannot log in unless you invite them to Nutshell.',
                location: 'settings-sidebar',
                adminOnly: true,
            },
            {
                settingsKey: 'removing_users',
                title: 'Removing users',
                description:
                    'Removed users cannot log into Nutshell. After you change your number of enabled users, be sure to update your plan.',
                location: 'settings-sidebar',
                cta: {
                    text: 'View billing details',
                    actionType: 'routed-link',
                    url: '/setup/billing/sales',
                },
                adminOnly: true,
            },
            {
                settingsKey: 'view_restrictions',
                title: 'Manage lead visibility with teams',
                description:
                    'Available in Pro plans and above, use teams to manage which contacts, leads and companies users can access.',
                location: 'settings-sidebar',
                adminOnly: true,
            },
            {
                settingsKey: 'unmanaged_users',
                title: 'What are unmanaged users?',
                description:
                    'Unmanged users are users that exist in Nutshell but aren’t in your company’s directory. For example, you might add contractors or users from another organization to Nutshell that aren’t in your company’s directory.',
                location: 'banner',
                adminOnly: true,
                requiredParam: 'status=4',
            },
            {
                settingsKey: 'unlicensed_users',
                title: 'What is an unlicensed user?',
                description:
                    'Unlicensed users are users that don’t have access to Nutshell because they haven’t been assigned a license. To enable a unlicensed user, assign them one or more licenses.',
                location: 'banner',
                adminOnly: true,
                requiredParam: 'status=3',
            },
        ],
    },
    '/setup/email-security': {
        cards: [
            {
                settingsKey: 'internal_domains',
                title: 'Internal domains',
                description:
                    'Emails that only include internal domain addresses will never sync into Nutshell. If your company uses multiple different domains, this is helpful for preventing internal communication from syncing into Nutshell.',
                location: 'settings-sidebar',
            },
            {
                settingsKey: 'global_blocklist',
                title: 'Global blocklist',
                description:
                    'This blocklist applies to all of your users, so every single message from these email addresses will stay out of Nutshell - no matter who is emailing with them. We recommend adding sensitive or private email addresses, like company lawyers or your payroll address, to this blocklist.',
                location: 'settings-sidebar',
            },
        ],
    },
    '/setup/sender-address': {
        cards: [
            {
                settingsKey: 'verified_emails',
                title: 'Verified emails',
                description:
                    'You’ll need to verify your email address before using it to send marketing emails with Nutshell.',
                location: 'settings-sidebar',
                adminOnly: true,
            },
            {
                settingsKey: 'sender_name',
                title: 'Sender name',
                description:
                    'You can create a custom sender name while setting up a verified sending address. When you send emails from Nutshell using that address, the sender name will appear in the recipient’s inbox as the “From” name.',
                location: 'settings-sidebar',
                adminOnly: true,
            },
            {
                settingsKey: 'improve_deliverability',
                title: 'Improve deliverability',
                description:
                    'Nutshell recommends consistently sending outreach messages from one or two sender addresses. The more you use a sender address, the better its reputation and deliverability.',
                location: 'settings-sidebar',
                adminOnly: true,
            },
            {
                settingsKey: 'free_email_addresses',
                title: 'Free email addresses',
                description:
                    'Services like Gmail, Yahoo, and other free email providers place limitations on which third parties can send emails on their behalf. We recommend that you send emails from your own domain name to avoid having your messages end up in spam folders.',
                location: 'settings-sidebar',
                adminOnly: true,
            },
            {
                settingsKey: 'dmarc_domains',
                title: 'DMARC domains',
                description:
                    'Certain domains place restrictions on who can send emails on their behalf. If your domain name is protected with DMARC, contact our support team for help authorizing Nutshell to send emails on your behalf.',
                location: 'settings-sidebar',
                adminOnly: true,
            },
        ],
    },
    '/setup/outcome': {
        cards: [
            {
                settingsKey: 'outcomes',
                title: 'What are outcomes?',
                description:
                    'When a lead is marked as Lost or Cancelled, users can provide a more specific reason. Use these outcomes for more powerful reporting.',
                location: 'settings-sidebar',
                adminOnly: true,
            },
        ],
    },
    '/setup/export': {
        cards: [
            {
                settingsKey: 'what_are_exports',
                title: 'What are these exports?',
                description: () => (
                    <div>
                        You can use this tool to generate an on-demand zip file of all your data.
                        You can use this export file for compliance with various legal frameworks,
                        and to ensure data portability.{' '}
                        <strong>At Nutshell, your data belongs to you.</strong>
                    </div>
                ),
                location: 'settings-sidebar',
                adminOnly: true,
            },
            {
                settingsKey: 'backup_data',
                title: 'Do I need to back up my data?',
                description: () => (
                    <div>
                        No. Nutshell continually makes low-level backups of your data.{' '}
                        <Link
                            variant='primary'
                            size='small'
                            newTab={true}
                            href='https://www.nutshell.com/security/'
                        >
                            Our infrastructure
                        </Link>{' '}
                        is always saving your data to multiple geographic locations.
                    </div>
                ),
                location: 'settings-sidebar',
                adminOnly: true,
            },
            {
                settingsKey: 'format_exports',
                title: 'What format are Nutshell exports?',
                description: () => (
                    <div>
                        We export your <strong>companies</strong>, <strong>people</strong>,{' '}
                        <strong>leads</strong>, <strong>activities</strong>, <strong>notes</strong>,{' '}
                        <strong>products</strong>, &amp; <strong>emails</strong> into separate CSV
                        files, bundled into a single zip file.
                    </div>
                ),
                location: 'settings-sidebar',
                adminOnly: true,
            },
            {
                settingsKey: 'api_access',
                title: 'API access',
                description: 'You can also generate and download exports via our API.',
                location: 'settings-sidebar',
                adminOnly: true,
            },
        ],
    },
    '/setup/activity-type': {
        cards: [
            {
                settingsKey: 'track_activity_types',
                title: 'Why track activity types?',
                description:
                    'Every activity has an activity type. You can define different benchmarks per activity type, for example, a sales rep should log 15 cold calls per week and 5 follow-up calls.',
                location: 'settings-sidebar',
                cta: {
                    text: 'View activity quotas',
                    actionType: 'routed-link',
                    url: '/sales/activity-quotas',
                },
                adminOnly: true,
            },
            {
                settingsKey: 'activity_type_reports',
                title: 'Reporting on activity types',
                description:
                    'The activity report helps you see what team members have been up to and how your team is performing against their quotas.',
                location: 'settings-sidebar',
                cta: {
                    text: 'View activity report',
                    actionType: 'routed-link',
                    url: '/reports/activity',
                },
                adminOnly: true,
            },
        ],
    },
    '/setup/source': {
        cards: [
            {
                settingsKey: 'getting_started_sources',
                title: 'Getting started with channels and sources',
                description:
                    'Existing sources can now be grouped into channels. These broad, predetermined categories describe where a lead comes from.',
                location: 'settings-sidebar',
                thumbnailPath: '/include/images/onboarding/channel-icons.svg',
                backgroundColor: 'plumXlt',
                cta: {
                    text: 'Learn more',
                    actionType: 'modal',
                    openableModalId: OpenableModals.ATTRIBUTION_ONBOARDING_MODAL,
                },
                adminOnly: true,
            },
            {
                settingsKey: 'what_is_source',
                title: 'What is a source?',
                description:
                    'Sources track how leads arrive at your website and learn about your business. Leads can be associated with one or more sources (e.g., ‘direct,’ ‘google,’ ‘web beta signup,’ etc.).',
                location: 'settings-sidebar',
                adminOnly: true,
            },
            {
                settingsKey: 'channel',
                title: 'What is a channel?',
                description:
                    'Channels group your sources into broader categories. Report on these channels to understand where your traffic comes from and identify opportunities for growth.',
                location: 'settings-sidebar',
                adminOnly: true,
            },
            {
                settingsKey: 'where_are_sources_from',
                title: 'Where do sources come from?',
                description:
                    'Sources can be added manually to ‘Traditional’ or ‘Referral traffic’ while sources in all other channels are tracked automatically with Nutshell Analytics.',
                location: 'settings-sidebar',
                adminOnly: true,
            },
        ],
    },
    '/setup/company-type': {
        cards: [
            {
                settingsKey: 'what_is_company_type',
                title: 'What is a company type?',
                description:
                    'Every company has a company type. This allows you to choose between Customers, Potential Customers, Partners, etc. The first listed type is the default.',
                location: 'settings-sidebar',
                adminOnly: true,
            },
            {
                settingsKey: 'prospects_to_customers',
                title: 'Convert "Prospects" to "Customers" when leads are won',
                description: () => (
                    <div>
                        Use our{' '}
                        <Link
                            // @ts-expect-error
                            as={RoutedLink}
                            variant='primary'
                            size='small'
                            newTab={true}
                            to='/setup/sales-automation'
                        >
                            Sales Automation
                        </Link>{' '}
                        to automatically change a company’s type to &#34;Customer&#34; (or any
                        company type!) when its lead is closed.
                    </div>
                ),
                location: 'settings-sidebar',
                adminOnly: true,
            },
        ],
    },
    '/setup/competitor': {
        cards: [
            {
                settingsKey: 'why_track_competitors',
                title: 'Why track competitors?',
                description:
                    'Tracking competitors with your leads makes it easy to see reports on your win rate against each competitor',
                location: 'settings-sidebar',
                adminOnly: true,
            },
            {
                settingsKey: 'process_triggers',
                title: 'Process triggers',
                description:
                    'In the Lead Distribution section you can configure a process to be automatically attached to a lead when competing with a specific competitor.',
                location: 'settings-sidebar',
                cta: {
                    text: 'Edit lead distribution',
                    actionType: 'routed-link',
                    url: '/setup/sales-automation',
                },
                adminOnly: true,
            },
        ],
    },
    '/setup/custom-field-old': {
        cards: [
            {
                settingsKey: 'what_is_custom_field',
                title: 'What is a custom field?',
                description:
                    'Custom fields allow you to collect and store additional information, beyond the Nutshell defaults. You can edit custom field values directly for companies and people.',
                location: 'settings-sidebar',
                adminOnly: true,
            },
            {
                settingsKey: 'what_is_decision',
                title: 'What is a decision?',
                description:
                    "This is a custom field that only allows for certain values. For example, you could have a field called 'shipper,' with the potential values of 'FedEx,' 'UPS,' etc.",
                location: 'settings-sidebar',
                adminOnly: true,
            },
            {
                settingsKey: 'lead_custom_fields',
                title: 'Lead custom fields',
                description:
                    'To collect data in a lead’s custom field, you must do so through a step. In the Process Builder, you can indicate a requirement for a step, and this requirement can be a custom field.',
                location: 'settings-sidebar',
                adminOnly: true,
            },
        ],
    },
    '/setup/custom-field': {
        cards: [
            {
                settingsKey: 'what_is_custom_field',
                title: 'What is a custom field?',
                description:
                    'Custom fields allow you to collect and store additional information, beyond the Nutshell defaults. You can edit custom field values directly for companies and people.',
                location: 'settings-sidebar',
                adminOnly: true,
                cta: {
                    text: 'Learn more about custom fields',
                    actionType: 'external-link',
                    url: 'https://support.nutshell.com/en/articles/8429015-how-to-create-and-manage-custom-fields',
                },
            },
        ],
    },
    '/setup/import-import2': {
        cards: [
            {
                settingsKey: 'import_2_import',
                title: 'What will Nutshell do with this data?',
                description: () => (
                    <div>
                        Everything you upload is covered by our{' '}
                        <Link
                            variant='primary'
                            size='small'
                            href='https://support.nutshell.com/en/articles/8428993-terms-conditions-nutshell-s-privacy-policy'
                        >
                            privacy policy
                        </Link>
                        . In short, your data belongs to you, and we won’t use it for anything.
                    </div>
                ),
                location: 'settings-sidebar',
            },
        ],
    },
    '/setup/import/products': {
        cards: [
            {
                settingsKey: 'dromo_product_importer_markets',
                title: 'How does Nutshell handle products in different markets?',
                description: () => (
                    <div>
                        If you’re a company that uses multiple markets, you will need to{' '}
                        <Link variant='primary' size='small' href='/setup/market' newTab={true}>
                            set those up
                        </Link>{' '}
                        ahead of time.
                    </div>
                ),
                location: 'settings-sidebar',
            },
            {
                settingsKey: 'dromo_importing_products',
                title: 'Importing products',
                description: () => (
                    <div>
                        See our{' '}
                        <Link
                            variant='primary'
                            size='small'
                            href='https://support.nutshell.com/en/articles/11321180-import-how-to-import-your-products-to-nutshell'
                            newTab={true}
                        >
                            full article
                        </Link>{' '}
                        for best practices on creating, updating, and deleting products.
                    </div>
                ),
                location: 'settings-sidebar',
            },
        ],
    },
    '/setup/industry': {
        cards: [
            {
                settingsKey: 'what_is_industry',
                title: 'What is an industry?',
                description:
                    'Every account can belong to a specific industry, allowing you to generate reports on leads per industry.',
                location: 'settings-sidebar',
                adminOnly: true,
            },
        ],
    },
    '/setup/market': {
        cards: [
            {
                settingsKey: 'what_is_market',
                title: 'What is a market?',
                description:
                    'Products have unique pricing for each market. Each lead has a specific market, so attached products receive the appropriate pricing.',
                location: 'settings-sidebar',
                adminOnly: true,
            },
            {
                settingsKey: 'multiple_currencies',
                title: 'Multiple currencies',
                description:
                    'Every market can use a different currency. Define a market with your currency type, then edit product pricing for that market.',
                location: 'settings-sidebar',
                adminOnly: true,
            },
            {
                settingsKey: 'default_markets',
                title: 'Default markets',
                description:
                    'Every user can set a default market in their My Account page. When they create a new lead, it will default to this setting.',
                location: 'settings-sidebar',
                adminOnly: true,
            },
        ],
    },
    '/sales/product': {
        cards: [
            {
                settingsKey: 'product_import_banner',
                title: 'Have your product list somewhere else?',
                description:
                    'Add or update existing products from a spreadsheet using our product importer',
                location: 'settings-sidebar',
                cta: {
                    text: 'Import products',
                    actionType: 'routed-link',
                    url: '/setup/import/products',
                },
                thumbnailPath: '/include/images/onboarding/product-illustration.svg',
                backgroundColor: 'blueLt',
            },
            {
                settingsKey: 'edit_product_pricing',
                title: 'How do I edit product pricing?',
                description:
                    'Products have unique pricing for every market. Each lead has a specific market, so attached products receive the appropriate pricing.',
                location: 'settings-sidebar',
                cta: {
                    text: 'Edit markets',
                    actionType: 'routed-link',
                    url: '/setup/market',
                },
                adminOnly: true,
            },
        ],
    },
    '/sales/product/categories': {
        cards: [
            {
                settingsKey: 'product_category',
                title: 'Product categories',
                description: 'Streamline your inventory by adding product categories',
                location: 'settings-sidebar',
                cta: {
                    text: 'Learn more',
                    actionType: 'external-link',
                    url: 'https://support.nutshell.com/en/articles/12521606-products-how-to-organize-product-categories',
                },
                thumbnailPath: '/include/images/onboarding/product-category-illustration.svg',
                backgroundColor: 'blueLt',
            },
        ],
    },
    '/setup/tag': {
        cards: [
            {
                settingsKey: 'what_are_tags',
                title: 'What are tags?',
                description:
                    'Use tags to classify and group your leads, companies, and people. Any user can add tags to an entity, and administrators can manage all tags.',
                location: 'settings-sidebar',
                cta: {
                    text: 'Read more about tags',
                    actionType: 'external-link',
                    url: 'https://support.nutshell.com/en/articles/8429016-tags-how-to-create-and-use-tags-in-nutshell',
                },
                adminOnly: true,
            },
        ],
    },
    '/setup/territory': {
        cards: [
            {
                settingsKey: 'what_is_territory',
                title: 'What is a territory?',
                description:
                    'Territories define specific geographic regions that your company uses to organize and assign leads. For example, create a territory called “New England” that includes Massachusetts and Vermont, or “New York Metro” that includes everything within 25 miles of New York City.',
                location: 'settings-sidebar',
                adminOnly: true,
            },
            {
                settingsKey: 'what_to_do_with_territory',
                title: 'What can I do with territories?',
                description: () => (
                    <div>
                        After you create one or more territories, you can use the{' '}
                        <Link
                            // @ts-expect-error
                            as={RoutedLink}
                            variant='primary'
                            size='small'
                            newTab={true}
                            to='/setup/sales-automation'
                        >
                            Lead Distribution
                        </Link>{' '}
                        page to assign new leads in a territory to a specific user or team. You can
                        also filter by territories from the leads, companies, or people tabs.
                    </div>
                ),
                location: 'settings-sidebar',
            },
            {
                settingsKey: 'territory_order',
                title: 'Territory order',
                description:
                    'Nutshell processes this list from the top down. Place your most precise territories at the top (e.g. cities and postal codes), and wider ranging territories at the bottom.',
                location: 'settings-sidebar',
                adminOnly: true,
            },
            {
                settingsKey: 'recalculating_territories',
                title: 'Recalculating territories',
                description:
                    'When you modify and save your territories, Nutshell will ask if you want to recalculate all territories using the new rules. Note that you can also manually change a person or company’s territory.',
                location: 'settings-sidebar',
                adminOnly: true,
            },
        ],
    },
    '/setup/quota': {
        cards: [
            {
                settingsKey: 'quotas_v2_migration',
                title: 'Updated sales quotas are now available',
                description:
                    'Instead of team-wide quotas, you can now assign goals directly to each user. Set monthly targets and update them throughout the fiscal year.',
                location: 'banner',
                thumbnailPath: '/include/illustrations/quota.svg',
                backgroundColor: 'blueLt',
                cta: {
                    text: 'Get started',
                    actionType: 'modal',
                    openableModalId: OpenableModals.QUOTAS_V2_ONBOARDING_MODAL,
                },
                isDismissable: false,
                adminOnly: true,
            },
            {
                settingsKey: 'assign_quotas',
                title: 'How do I assign quotas?',
                description:
                    'Quotas are assigned separately for each team. Once assigned to a team, quotas can be optionally distributed to individual users.',
                location: 'settings-sidebar',
                cta: {
                    text: 'View sales forecast report',
                    actionType: 'routed-link',
                    url: '/reports/forecast',
                },
                adminOnly: true,
            },
            {
                settingsKey: 'quotas_work',
                title: 'How do quotas work?',
                description:
                    'Quotas are based on your company’s fiscal year. When a lead is marked as Won, the value of that lead goes toward the assigned user’s quota.',
                location: 'settings-sidebar',
                adminOnly: true,
            },
            {
                settingsKey: 'quotas_by_user',
                title: 'Track quotas by user',
                description:
                    'By default, quotas are only calculated on a team basis. To track by user, use the distribution slider to adjust the individual’s quota. Note that future individual quotas may change as users are added and removed from teams.',
                location: 'settings-sidebar',
                adminOnly: true,
            },
        ],
    },
    '/setup/activity-quota': {
        cards: [
            {
                settingsKey: 'what_is_activity_quota',
                title: 'What is an activity quota?',
                description:
                    'Provide a benchmark of logged activities for specific users. You can view activity reports that track how many activities a user has logged, versus their quota. Activity quotas are defined and tracked on a weekly basis.',
                location: 'settings-sidebar',
                cta: {
                    text: 'View activity report',
                    actionType: 'routed-link',
                    url: '/reports/activity',
                },
                adminOnly: true,
            },
        ],
    },
    '/setup/api-key': {
        cards: [
            {
                settingsKey: 'what_is_api_key',
                title: 'What is an API key?',
                description:
                    'Use an API key to allow third-party systems to communicate with Nutshell. For example, you can have a web form automatically create a new lead when a user fills it out, or retrieve a user’s leads.',
                location: 'settings-sidebar',
                adminOnly: true,
            },
            {
                settingsKey: 'api_documentation',
                title: 'API documentation',
                description: 'Full information about how to communicate with the Nutshell API.',
                location: 'settings-sidebar',
                cta: {
                    text: 'Explore API documentation',
                    actionType: 'external-link',
                    url: 'https://developers.nutshell.com',
                },
                adminOnly: true,
            },
            {
                settingsKey: 'api_permissions',
                title: 'Permissions',
                description:
                    'Standard keys can access all Nutshell APIs. Keys with impersonation privileges can perform all request on behalf of any Nutshell user. Form submission keys are only capable of creating new entities via the simple RESTful API.',
                location: 'settings-sidebar',
                adminOnly: true,
            },
        ],
    },
    '/setup/integration': {
        sidebarTitle: 'Integrations',
        cards: [
            {
                settingsKey: 'start_app_connect',
                title: 'Let us do the work for you with AppConnect',
                description:
                    'Nutshell’s AppConnect allows you to integrate your data and worflows between Nutshell and the applications your company already uses.',
                location: 'glossary-only',
                thumbnailPath: '/include/images/onboarding/nutshell-app-connect.jpeg',
                backgroundColor: 'blueLt',
                thumbnailPosition: 'full',
                cta: {
                    text: 'Learn more',
                    actionType: 'external-link',
                    url: 'https://www.nutshell.com/appconnect',
                },
            },
        ],
    },
    '/sales/sales-quotas': {
        cards: [
            {
                settingsKey: 'quotas_v2_migration_success',
                title: 'Updated sales quotas are now available',
                description:
                    'Instead of team-wide quotas, you can now assign goals directly to each user. Set monthly targets and update them throughout the fiscal year.',
                location: 'banner',
                thumbnailPath: '/include/illustrations/quota.svg',
                backgroundColor: 'blueLt',
                cta: {
                    text: 'Learn more',
                    actionType: 'modal',
                    openableModalId: OpenableModals.QUOTAS_V2_ONBOARDING_MODAL,
                },
                isDismissable: true,
                adminOnly: true,
            },
            {
                settingsKey: 'quotas_work',
                title: 'How do quotas work?',
                description:
                    'Quotas are based on your company’s fiscal year. When a lead is marked as Won, the value of that lead goes toward the assigned user’s quota.',
                location: 'settings-sidebar',
                adminOnly: true,
                cta: {
                    text: 'Learn more',
                    actionType: 'external-link',
                    url: 'https://support.nutshell.com',
                },
            },
        ],
    },
    '/sales/activity-quotas': {
        cards: [
            {
                settingsKey: 'what_is_activity_quota',
                title: 'What is an activity quota?',
                description:
                    'Provide a benchmark of logged activities for specific users. You can view activity reports that track how many activities a user has logged, versus their quota. Activity quotas are defined and tracked on a weekly basis.',
                location: 'settings-sidebar',
                cta: {
                    text: 'View activity report',
                    actionType: 'routed-link',
                    url: '/reports/activity',
                },
                adminOnly: true,
            },
        ],
    },
    '/sales/lead-outcomes': {
        cards: [
            {
                settingsKey: 'outcomes',
                title: 'What are outcomes?',
                description:
                    'When a lead is marked as Lost or Cancelled, users can provide a more specific reason. Use these outcomes for more powerful reporting.',
                location: 'settings-sidebar',
                adminOnly: true,
            },
        ],
    },
    '/sales/pipelines': {
        sidebarTitle: 'Pipelines track your sales processes through different stages',
        cards: [
            {
                settingsKey: 'defaultStagesetOnboardingWizard',
                title: 'Design a pipeline around your sales process',
                description:
                    'Answer a few questions about your business and we’ll help you automatically generate a pipeline.',
                location: 'banner',
                thumbnailPath: '/include/images/onboarding/lead-arrow-group.svg',
                backgroundColor: 'yellow',
                cta: {
                    text: 'Get started',
                    openableModalId: OpenableModals.DESIGN_PIPELINE_WIZARD,
                    actionType: 'modal',
                },
                adminOnly: true,
            },
            {
                settingsKey: 'understandingPipelines',
                title: 'What is a pipeline?',
                description:
                    'Understand the autonomy of a pipeline and how to utilize each feature',
                location: 'banner',
                thumbnailPosition: 'centered',
                thumbnailPath: '/include/images/onboarding/pipeline.svg',
                backgroundColor: 'plumDk',
                cta: {
                    text: 'Learn more',
                    actionType: 'modal',
                    openableModalId: OpenableModals.BOARD_ONBOARDING_MODAL,
                },
            },
            {
                settingsKey: 'templateDownloads',
                title: 'What makes a good pipeline?',
                description: 'We have 16 examples of sales pipelines to get you started.',
                location: 'banner',
                thumbnailPosition: 'full',
                thumbnailPath: '/include/images/onboarding/pipeline-templates.jpg',
                backgroundColor: 'blueLt',
                cta: {
                    text: 'View templates',
                    actionType: 'external-link',
                    url: '/dotcom/process-templates',
                },
            },
            {
                settingsKey: 'pipelineBenefits',
                title: 'Explore how a pipeline could benefit your company',
                description: 'Create a new pipeline or view 5 examples to get you started',
                location: 'banner',
                thumbnailPosition: 'centered',
                thumbnailPath: '/include/images/onboarding/lead-arrow-group.svg',
                backgroundColor: 'plumLt',
                cta: {
                    text: 'Get started',
                    openableModalId: OpenableModals.CREATE_PIPELINE_MODAL,
                },
            },
        ],
    },
    '/marketing/email/broadcasts': {
        sidebarTitle: 'Broadcasts are one-time email messages sent out to an audience',
        cards: [
            {
                settingsKey: 'broadcasts_info_modal',
                title: 'What are broadcasts?',
                description: 'Design and send one-time email messages to an audience.',
                location: 'banner',
                thumbnailPath: '/include/images/onboarding/single-purple-envelope-email.svg',
                backgroundColor: 'plumXlt',
                cta: {
                    text: 'Learn more',
                    openableModalId: OpenableModals.BROADCAST_INFO_MODAL,
                    actionType: 'modal',
                },
            },
            {
                settingsKey: 'configure_dns_for_gmail',
                title: 'Configure additional DNS records to continue sending outreach to Gmail users',
                description:
                    'Starting in February, Google, Yahoo, and AOL will begin requiring DMARC settings',
                location: 'banner',
                thumbnailPath: '/include/images/onboarding/warning-icon.svg',
                backgroundColor: 'yellow',
                cta: {
                    text: 'Learn more',
                    actionType: 'external-link',
                    url: 'https://www.nutshell.com/blog/upcoming-dmarc-email-authentication-requirements',
                },
                adminOnly: true,
            },
        ],
    },
    '/marketing/email/newsletters': {
        sidebarTitle: 'Newsletters are an ongoing series of messages that follow a similar theme',
        cards: [
            {
                settingsKey: 'newsletter_info_modal',
                title: 'What are newsletters?',
                description: 'Learn about sending regular correspondence to an audience',
                location: 'banner',
                thumbnailPath: '/include/images/onboarding/open-envelope.svg',
                backgroundColor: 'plumXlt',
                cta: {
                    text: 'Learn more',
                    openableModalId: OpenableModals.NEWSLETTER_INFO_MODAL,
                    actionType: 'modal',
                },
            },
        ],
    },
    '/marketing/email/drip-sequences': {
        sidebarTitle:
            'Drip sequences are a series of messages that automatically send based on how you configure it',
        cards: [
            {
                settingsKey: 'drip_sequences_info_modal',
                title: 'What is a drip sequence?',
                description: 'Automate a series of messages to send at a set cadence.',
                location: 'banner',
                thumbnailPath: '/include/images/onboarding/drip-sequence-doodle.svg',
                backgroundColor: 'plumXlt',
                cta: {
                    text: 'Learn more',
                    openableModalId: OpenableModals.DRIP_SEQUENCE_INFO_MODAL,
                    actionType: 'modal',
                },
            },
        ],
    },
    '/marketing/analytics': {
        sidebarTitle:
            'Analytics brings you additional information about what your leads were doing before they became a lead',
        cards: [
            {
                settingsKey: 'analytics_onboarding_modal',
                title: 'Learn where your leads come from with Nutshell Analytics',
                description:
                    'Knowing where your leads come from can help your team make the right sales and marketing decisions.',
                location: 'banner',
                thumbnailPath: '/include/images/onboarding/analytics-info.svg',
                backgroundColor: 'plumXlt',
                cta: {
                    text: 'Learn more',
                    actionType: 'modal',
                    openableModalId: OpenableModals.ANALYTICS_ONBOARDING_MODAL,
                },
            },
            {
                settingsKey: 'analytics_setup_wizard',
                title: 'Get started by setting up tracking',
                description:
                    'We provide you with a small code snippet to embed on your website to start tracking site visitors.',
                location: 'glossary-only',
                thumbnailPath: '/include/images/onboarding/embed-snippet.svg',
                backgroundColor: 'tagOrangeLt',
                cta: {
                    text: 'Set up tracking',
                    openableModalId: OpenableModals.EMBED_ANALYTICS_MODAL,
                },
            },
        ],
    },
    '/marketing/visitors/list': {
        sidebarTitle:
            'VisitorIQ identifies the companies and individuals exploring your website before they even fill out a form',
        cards: [
            {
                settingsKey: 'what_is_visitor_iq',
                title: 'What is VisitorIQ?',
                description:
                    'A powerful prospecting tool to track and convert website visitors into sales opportunities.',
                location: 'glossary-only',
                thumbnailPath: '/include/images/onboarding/visitor-iq.svg',
                backgroundColor: 'plumXlt',
                cta: {
                    text: 'Learn more',
                    openableModalId: OpenableModals.VISITOR_IQ_ONBOARDING_MODAL,
                    actionType: 'modal',
                },
            },
        ],
    },
    '/marketing/automations': {
        sidebarTitle: 'Marketing automations',
        cards: [
            {
                settingsKey: 'what_are_marketing_automations',
                title: 'What are marketing automations?',
                description:
                    'Configure automations that trigger when leads visit specific pages on your website.',
                location: 'glossary-only',
                thumbnailPath: '/include/images/onboarding/visitor-iq.svg',
                backgroundColor: 'plumXlt',
                cta: {
                    text: 'Learn more',
                    actionType: 'external-link',
                    url: 'https://support.nutshell.com/en/articles/13460946-marketing-marketing-automations',
                    newTab: true,
                },
            },
        ],
    },
    '/marketing/competitors/charts': {
        sidebarTitle:
            'CompetitorSpy is a powerful tool for comparing your website’s performance with up to five competitors’',
        cards: [
            {
                settingsKey: 'elevate_insights',
                title: 'Elevate your insights with CompetitorSpy',
                description:
                    'Gain an edge over your top 5 competitors with detailed competitive analysis.',
                location: 'banner',
                thumbnailPath: '/include/images/onboarding/competitor-iq-ball-roll.svg',
                backgroundColor: 'plumXlt',
                cta: {
                    text: 'Learn more',
                    openableModalId: OpenableModals.COMPETITOR_IQ_INFO_MODAL,
                    actionType: 'modal',
                },
            },
        ],
    },
    '/marketing/forms': {
        sidebarTitle:
            'Forms can be embedded on a site to collect contact information and automatically create new people, companies, and leads',
        cards: [
            {
                settingsKey: 'what_are_forms',
                title: 'What are forms?',
                description: 'Embed on a site to collect contacts and other information.',
                location: 'banner',
                thumbnailPath: '/include/images/onboarding/forms.svg',
                backgroundColor: 'plumXlt',
                cta: {
                    text: 'Learn more',
                    openableModalId: OpenableModals.FORMS_INFO_MODAL,
                    actionType: 'modal',
                },
            },
        ],
    },
    '/marketing/connected-forms': {
        sidebarTitle:
            'Connected forms let you capture submission data from forms created with other tools',
        cards: [
            {
                settingsKey: 'what_are_connected_forms',
                title: 'What are connected forms?',
                description: 'Capture submission data from forms created with other tools.',
                location: 'banner',
                thumbnailPath: '/include/images/onboarding/connected-forms.svg',
                backgroundColor: 'plumXlt',
                cta: {
                    text: 'Learn more',
                    openableModalId: OpenableModals.CONNECTED_FORMS_INFO_MODAL,
                    actionType: 'modal',
                },
            },
        ],
    },
    '/marketing/landing-pages': {
        sidebarTitle: 'Begin creating engaging pages',
        cards: [
            {
                settingsKey: 'landing-pages',
                title: 'Begin creating engaging pages',
                description:
                    'Explore our comprehensive features for designing landing pages that deliver results.',
                location: 'banner',
                thumbnailPosition: 'no-resize',
                thumbnailPath: '/include/images/onboarding/landing-pages-video.png',
                backgroundColor: 'yellow',
                cta: {
                    text: 'Learn more',
                    actionType: 'video',
                    youTubeVideoId: 'H96Tl5iEbQw',
                },
            },
        ],
    },
    '/marketing/domains': {
        sidebarTitle:
            'Configure additional DNS records to continue sending outreach to Gmail users',
        cards: [
            {
                settingsKey: 'domains',
                title: 'Domains',
                description: () => (
                    <div>
                        Add and set up a custom domain to host your{' '}
                        <Link
                            // @ts-expect-error
                            as={RoutedLink}
                            variant='primary'
                            size='small'
                            to='/email-marketing/landing-pages'
                            newTab={true}
                        >
                            pages
                        </Link>
                        . This not only improves brand recognition, but also enhances email
                        deliverability.
                    </div>
                ),
                location: 'settings-sidebar',
            },
            {
                settingsKey: 'click_trackings',
                title: 'Sensitive addresses',
                description: () => (
                    <div>
                        Keep track of how many recipients interact with the links in your emails. By
                        using a{' '}
                        <Link
                            variant='primary'
                            size='small'
                            href='https://support.nutshell.com/en/articles/8428873-campaigns-how-to-set-up-dkim-spf-and-dmarc'
                            newTab={true}
                        >
                            custom tracking domain
                        </Link>{' '}
                        (e.g. ’clickyourdomain.com’), you can personalize the campaign links and
                        associate your domain’s reputation with the emails you send.
                    </div>
                ),
                location: 'settings-sidebar',
            },
        ],
    },
    '/marketing/sender-address': {
        cards: [
            {
                settingsKey: 'verified_emails',
                title: 'Verified emails',
                description:
                    'You’ll need to verify your email address before using it to send marketing emails with Nutshell.',
                location: 'settings-sidebar',
                adminOnly: true,
            },
            {
                settingsKey: 'sender_name',
                title: 'Sender name',
                description:
                    'You can create a custom sender name while setting up a verified sending address. When you send emails from Nutshell using that address, the sender name will appear in the recipient’s inbox as the “From” name.',
                location: 'settings-sidebar',
                adminOnly: true,
            },
            {
                settingsKey: 'improve_deliverability',
                title: 'Improve deliverability',
                description:
                    'Nutshell recommends consistently sending outreach messages from one or two sender addresses. The more you use a sender address, the better its reputation and deliverability.',
                location: 'settings-sidebar',
                adminOnly: true,
            },
            {
                settingsKey: 'free_email_addresses',
                title: 'Free email addresses',
                description:
                    'Services like Gmail, Yahoo, and other free email providers place limitations on which third parties can send emails on their behalf. We recommend that you send emails from your own domain name to avoid having your messages end up in spam folders.',
                location: 'settings-sidebar',
                adminOnly: true,
            },
            {
                settingsKey: 'dmarc_domains',
                title: 'DMARC domains',
                description:
                    'Certain domains place restrictions on who can send emails on their behalf. If your domain name is protected with DMARC, contact our support team for help authorizing Nutshell to send emails on your behalf.',
                location: 'settings-sidebar',
                adminOnly: true,
            },
        ],
    },
    '/marketing/seo-scan': {
        cards: [
            {
                settingsKey: 'seo-scan',
                title: 'Learn about SEO audits',
                description: 'Use WebFX‘s free SEO audit tool for an SEO analysis in minutes.',
                location: 'settings-sidebar',
                thumbnailPosition: 'full',
                thumbnailPath: '/include/images/onboarding/seo-scan-video.jpg',
                backgroundColor: 'green',
                cta: {
                    text: 'Watch video',
                    actionType: 'video',
                    youTubeVideoId: 'upxbrEC0whA',
                },
            },
        ],
    },
    '/reports/tasks': {
        sidebarTitle: 'Track team performance with the task report',
        cards: [
            {
                settingsKey: 'tasks_report_cue_card',
                title: 'How does the task report work?',
                description:
                    'Gain a high-level overview of task completion along with a detailed breakdown for each pipeline.',
                location: 'banner',
                backgroundColor: 'blueXlt',
                thumbnailPosition: 'centered',
                thumbnailPath: '/include/images/onboarding/tasks-report.svg',
                cta: {
                    text: 'Learn more',
                    actionType: 'external-link',
                    url: 'https://support.nutshell.com/en/articles/12542645-sales-reporting-track-team-performance-with-the-task-report',
                },
            },
        ],
    },
    '/reports/engagement': {
        sidebarTitle: 'See insights on actions, leads, and sales in one place.',
        cards: [
            {
                settingsKey: 'engagement_report_cue_card',
                location: 'settings-sidebar',
                backgroundColor: 'blueXlt',
                thumbnailPosition: 'full',
                thumbnailPath: '/include/images/onboarding/engagement-report.svg',
                title: 'How does the engagement report work?',
                description:
                    'Understand what each stage of the funnel means and how a visitor moves from a visit to becoming a lead or sale.',
                cta: {
                    text: 'Open the slideshow',
                    actionType: 'modal',
                    openableModalId: OpenableModals.ENGAGEMENT_REPORT_ONBOARDING_MODAL,
                },
            },
        ],
    },
    '/my-account/phone': {
        sidebarTitle: 'Configure your phone system integrations',
        cards: [
            {
                settingsKey: 'ringcentral-appconnect',
                title: 'RingCentral AppConnect',
                description:
                    'Connect Nutshell to your RingCentral account through the AppConnect extension.',
                location: 'settings-sidebar',
                thumbnailPath: '/include/images/mcp-client-logos/ringcentral-logo.svg',
                thumbnailPosition: 'no-resize',
                backgroundColor: 'blueLt',
                cta: {
                    text: 'Learn more',
                    actionType: 'external-link',
                    url: 'https://support.nutshell.com/en/articles/13573925-ringcentral-appconnect-make-and-log-calls-directly-from-nutshell',
                },
            },
            {
                settingsKey: 'click-to-call-setup',
                title: 'Nutshell Click-to-Call',
                description:
                    'Make and log unlimited calls from your browser with AI-powered summaries and transcriptions.',
                location: 'settings-sidebar',
                thumbnailPath: '/include/images/external-meetings/click-to-call.png',
                thumbnailPosition: 'no-resize',
                backgroundColor: 'plumXlt',
                cta: {
                    text: 'Learn more',
                    actionType: 'external-link',
                    url: 'https://www.nutshell.com/blog/elevate-your-workflow-with-ai-powered-click-to-call',
                },
            },
            {
                settingsKey: 'phone-integrations',
                title: 'Native call syncing integrations',
                description: 'Connect your phone system to Nutshell to sync and summarize calls.',
                location: 'settings-sidebar',
                thumbnailPath: '/include/images/external-meetings/phone-integrations.svg',
                thumbnailPosition: 'no-resize',
                backgroundColor: 'blueLt',
                cta: {
                    text: 'Learn more',
                    actionType: 'external-link',
                    url: 'https://www.nutshell.com/integrations',
                },
            },
        ],
    },
};
